import { defineMessages, useIntl } from 'react-intl';
import { Loading, Modal } from '@carbon/react';
import { useState } from 'react';

import styles from './Team.module.scss';

import CurrentTeamMembers from 'Screens/Dashboard/Team/Components/CurrentTeamMembers/CurrentTeamMembers';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import sharedMessages from 'Components/utils/sharedMessages';
import { useGetTeamMembersQuery, useKickAllNonPrivilegedUsersMutation } from 'api/entityEndpoints';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import useGetUser from 'hooks/useGetUser';

const Team = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const {
        data: teamMembersPage,
        isFetching,
        isError,
        refetch,
    } = useGetTeamMembersQuery(
        {
            entityId: currentEntityId ?? '',
            limit: pageSize,
            offset: (page - 1) * pageSize,
            query: search.trim() || undefined,
        },
        { skip: !currentEntityId },
    );
    const [kickAllOpen, setKickAllOpen] = useState(false);
    const [kickAll, { isLoading: isBulkKicking }] = useKickAllNonPrivilegedUsersMutation();

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handlePageChange = ({ page: nextPage, pageSize: nextPageSize }: { page: number; pageSize: number }) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    };

    const handleRefetch = () => {
        refetch().then().catch(() => {
            // Error is handled by isError state, so we can ignore it here
        });
    };

    const onKickAll = async () => {
        if (!currentEntityId) {
            return;
        }

        await kickAll({ entityId: currentEntityId }).unwrap();
        setKickAllOpen(false);
    };

    return (
        <div className={styles.container}>
            {
                (() => {
                    if (isFetching) {
                        return <Loading />;
                    }

                    if (isError) {
                        return <ErrorActionState onClick={handleRefetch} className={styles.error} />;
                    }
                    return (
                        <CurrentTeamMembers
                            members={teamMembersPage?.items ?? []}
                            search={search}
                            page={page}
                            pageSize={pageSize}
                            totalItems={teamMembersPage?.totalItems ?? 0}
                            onSearchChange={handleSearchChange}
                            onPageChange={handlePageChange}
                        />
                    );
                })()
            }
            <Modal
                preventCloseOnClickOutside
                open={kickAllOpen}
                onRequestClose={() => setKickAllOpen(false)}
                onRequestSubmit={onKickAll}
                primaryButtonText={intl.formatMessage(messages.kickAllConfirm)}
                secondaryButtonText={intl.formatMessage(messages.cancel)}
                primaryButtonDisabled={isBulkKicking}
                modalHeading={intl.formatMessage(messages.kickAllHeading)}
                modalLabel={intl.formatMessage(messages.kickAllLabel)}
                danger
            >
                {intl.formatMessage(messages.kickAllDescription, { count: teamMembersPage?.removableCount ?? 0 })}
            </Modal>
        </div>
    );
};

const TeamStateHandler = () => {
    const currentEntityId = useGetCurrentEntityId();
    const intl = useIntl();

    const user = useGetUser();
            
    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    if (!currentEntityId) {
        return <EmptyState title={intl.formatMessage(sharedMessages.noEntitySelected)} />;
    }

    return <Team />;
};

const messages = defineMessages({
    description: {
        id: 'src.Screens.Dashboard.Team.Team.description',
        defaultMessage:
            'Tutaj możesz zarządzać dostępem.',
    },
    addNew: {
        id: 'src.Screens.Dashboard.Team.Team.addNew',
        defaultMessage: 'Zaproś',
    },
    kickAll: {
        id: 'src.Screens.Dashboard.Team.Team.kickAll',
        defaultMessage: 'Usuń wszystkich',
    },
    kickAllHeading: {
        id: 'src.Screens.Dashboard.Team.Team.kickAllHeading',
        defaultMessage: 'Usuwanie wielu użytkowników',
    },
    kickAllLabel: {
        id: 'src.Screens.Dashboard.Team.Team.kickAllLabel',
        defaultMessage: 'Operacja zbiorcza',
    },
    kickAllDescription: {
        id: 'src.Screens.Dashboard.Team.Team.kickAllDescription',
        defaultMessage: 'Czy na pewno chcesz usunąć {count} użytkowników (z wyłączeniem Owner/Admin/Moderator)?',
    },
    kickAllConfirm: {
        id: 'src.Screens.Dashboard.Team.Team.kickAllConfirm',
        defaultMessage: 'Usuń wszystkich',
    },
    cancel: {
        id: 'src.Screens.Dashboard.Team.Team.cancel',
        defaultMessage: 'Anuluj',
    },

});

export default TeamStateHandler;
